/**
 * Aggressive Wander Behavior
 * 
 * NPC wanders randomly within configured bounds.
 * Attacks player when detected within range.
 */

import type { NPCBehavior, NPCBehaviorConfig } from './NPCBehavior';
import { wanderTick, createWanderState, type WanderState } from '../presets/movement';
import { 
  aggressiveDetect, 
  aggressiveTick, 
  defaultDamage,
  defaultDeath,
  createCombatState 
} from '../presets/combat';
import type { AITickAPI } from '../hooks/NPCHookAPI';

interface BoundedWanderState extends WanderState {
  minT: number;
  maxT: number;
}

/**
 * Bounded wander tick - wanders randomly but stays within T bounds
 */
const boundedWanderTick = (api: AITickAPI, state: BoundedWanderState): void => {
  // Check if at bounds - reverse if needed
  if (api.npcT >= state.maxT && state.direction > 0) {
    state.direction = -1;
    state.timer = 0;
  } else if (api.npcT <= state.minT && state.direction < 0) {
    state.direction = 1;
    state.timer = 0;
  }
  
  wanderTick(api, state);
};

/**
 * Create an aggressive wander behavior instance.
 */
export const createAggressiveWander = (config: NPCBehaviorConfig): NPCBehavior => {
  // State initialized in onSpawn
  let wanderState: BoundedWanderState | null = null;
  
  const combatState = createCombatState(
    config.attackRange ?? 5.0,
    config.attackCooldown ?? 1.0
  );
  
  let inCombat = false;
  
  return {
    id: 'aggressive_wander',
    displayName: 'Aggressive Wanderer',
    
    onSpawn: (api) => {
      const spawnT = api.spawnT;
      const splineLength = api.splineLength;
      
      const leftDistance = config.patrolLeft ?? 5;
      const rightDistance = config.patrolRight ?? 5;
      const leftT = leftDistance / splineLength;
      const rightT = rightDistance / splineLength;
      
      // Wander state with longer wait times (less movement)
      const baseState = createWanderState(2.0, 5.0);
      wanderState = {
        ...baseState,
        minT: Math.max(0, spawnT - leftT),
        maxT: Math.min(1, spawnT + rightT),
      };
      
      inCombat = false;
      combatState.cooldownRemaining = 0;
      combatState.attackLockTimer = 0;
    },
    
    onTick: (api) => {
      if (!wanderState) {
        // Fallback if onSpawn wasn't called
        wanderState = {
          ...createWanderState(2.0, 5.0),
          minT: 0,
          maxT: 1,
        };
      }
      
      let inAttackLock = false;
      
      if (inCombat) {
        // In combat: attack (returns true if locked in attack animation)
        inAttackLock = aggressiveTick(api, combatState);
      }
      
      // Only wander if not locked in attack
      if (!inAttackLock) {
        boundedWanderTick(api, wanderState);
      }
    },
    
    onPlayerDetected: (api) => {
      inCombat = true;
      aggressiveDetect(api);
    },
    
    onPlayerLost: (_api) => {
      inCombat = false;
    },
    
    onDamage: defaultDamage,
    
    onDeath: defaultDeath,
  };
};
